import type { ToolbarSettings, BISettings, RichContent } from '@wix/ricos';
import type Experiments from '@wix/wix-experiments';
import type { useBi } from '@wix/yoshi-flow-editor';
import type { ErrorInfo } from 'react';
import React, { Component } from 'react';

import copyTextToClipboard from '../utils/copy-text-to-clipboard';
import ActionButton from './action-button';
import { tpaBISettings } from './bi-settings';
import { ContentCreator } from './content-creator';
import Editor from './editor';
import { getStyleFromTextPreset } from './get-style-from-text-preset';
import Viewer from './viewer';
import styles from './widget-styles.scss';
import { withSettings } from './with-settings';

type WixColor = {
  name: string;
  reference: string;
  value: string;
};

type WixPalette = WixColor[];

type RicosDemoProps = {
  content?: RichContent;
  publish: (content: RichContent) => Promise<boolean>;
  locale: string;
  siteColors: WixPalette;
  defaultParams: BISettings['defaultParams'];
  siteTextPresets: any;
  styleParams: any;
  instance: string;
  mobile: boolean;
  isAdmin: boolean;
  toolbarSettings?: ToolbarSettings;
  isInSEO?: boolean;
  parentClass?: string;
  selectedPlugins?: Record<string, boolean>;
  useNewPluginMenu?: boolean;
  useStickyToolbar?: boolean;
  useFontSize?: boolean;
  useLinkToSection?: boolean;
  useVerticalSlimLayout?: boolean;
  clickToTweet?: boolean;
  showImportExport?: boolean;
  experiments?: Experiments;
  logger: ReturnType<typeof useBi>;
};

type RicosDemoState = {
  isEditing: boolean;
  isImporting: boolean;
  importError: boolean;
  importValue: string;
  exportSuccess: boolean;
  content?: RichContent;
};

class RicosDemo extends Component<RicosDemoProps, RicosDemoState> {
  content?: RichContent;
  editor?: any;

  constructor(props: RicosDemoProps) {
    super(props);
    const { content } = props;
    this.state = {
      isEditing: !content,
      isImporting: false,
      importError: false,
      importValue: 'Enter Content State here...',
      content,
      exportSuccess: false,
    };
  }

  publish = async (content: RichContent) => {
    console.log('PUBLISH (ricosDemp)', content);
    let errorContent;
    try {
      await this.props.publish(content);
    } catch (e) {
      console.error(e);
      const errContentObj = new ContentCreator();
      errContentObj.addContentErrorMessage({
        content: this.state.content,
        error: e.message,
        componentStack: e.stack,
      });

      errorContent = errContentObj.json;
    }

    this.setState({
      content: errorContent || content,
      isEditing: false,
    });
  };

  exportContent = () => {
    copyTextToClipboard(JSON.stringify(this.state.content)).then(
      () => {
        console.log('copied to clipboard');
        this.setState({ exportSuccess: true });
        setTimeout(() => this.setState({ exportSuccess: false }), 2000);
      },
      err => {
        console.log('export failed', err);
      }
    );
  };

  displayImportPanel = () => this.setState({ isImporting: true });
  onImportChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    this.setState({ importValue: event.target.value, importError: false });

  importContent = () => {
    try {
      this.setState({
        content: JSON.parse(this.state.importValue),
        isImporting: false,
      });
    } catch {
      this.setState({ importError: true });
    }
  };
  onEdit = () => this.setState({ isEditing: true });
  switchToView = () => this.setState({ isEditing: false });

  componentDidCatch(error: Error, { componentStack }: ErrorInfo) {
    console.error(error.toString());
    console.error(componentStack);
    const content = new ContentCreator();
    content.addContentErrorMessage({
      content: this.state.content,
      error,
      componentStack: componentStack || '',
    });

    // content;
    this.setState({
      content: content.json,
    });
  }

  render() {
    const {
      locale,
      siteColors,
      siteTextPresets,
      instance,
      mobile,
      isAdmin,
      parentClass,
      toolbarSettings,
      isInSEO,
      useNewPluginMenu,
      useStickyToolbar,
      useFontSize,
      useLinkToSection,
      useVerticalSlimLayout,
      clickToTweet,
      selectedPlugins = {},
      showImportExport,
      styleParams = {},
      experiments,
      defaultParams,
      logger,
    } = this.props;
    const { isEditing, isImporting, importValue, content, importError, exportSuccess } = this.state;

    const { fonts = {}, colors = {} } = styleParams;
    const { textFont } = fonts;
    const { textColor } = colors;
    const colorObj = { color: textColor?.value };

    const customStyles = siteTextPresets
      ? {
          /*
          possible mappings:
            H1 == “Title”
            H2 == “Page-title”
            H3 == “Heading-XL”
            H4 == “Heading-L”
            H5 == “Heading-M”
            H6 == “Heading-S”
            P1 == “Body-M”
            P2 == “Body-S”
            P3 == “Body-XS”
      */
          h2: getStyleFromTextPreset(siteTextPresets['Page-title'], colorObj),
          h3: getStyleFromTextPreset(siteTextPresets['Heading-XL'], colorObj),
          h4: getStyleFromTextPreset(siteTextPresets['Heading-L'], colorObj),
          h5: getStyleFromTextPreset(siteTextPresets['Heading-M'], colorObj),
          h6: getStyleFromTextPreset(siteTextPresets['Heading-S'], colorObj),
          p: {
            fontSize: textFont?.size + 'px',
            color: textColor?.value,
          },
        }
      : {};

    const typography = {
      fontFamily: textFont?.family,
    };

    const commonProps = {
      locale,
      instance,
      theme: { palette: siteColors, parentClass, customStyles, typography },
      defaultParams,
      isMobile: mobile,
      content,
      selectedPlugins,
      useLinkToSection,
      useVerticalSlimLayout,
      wixExperiments: experiments?.all(),
      biSettings: tpaBISettings(defaultParams, logger),
      linkPanelSettings: {
        showNewTabCheckbox: true,
        showNoFollowCheckbox: true,
        showNoReferrerCheckbox: true,
        showSponsoredCheckbox: true,
      },
    };

    const mediaSettings = {
      fullscreenProps: {
        backgroundColor: siteColors && siteColors[5]?.value,
        foregroundColor: siteColors && siteColors[9]?.value,
      },
    };

    const useStaticTextToolbar = toolbarSettings?.useStaticTextToolbar || useStickyToolbar;
    const ViewerEditor = isEditing ? (
      <div data-hook="ricos-editor">
        <Editor
          {...commonProps}
          toolbarSettings={{ ...toolbarSettings, useStaticTextToolbar }}
          isAdmin={isAdmin}
          onPublish={this.publish}
          onCancel={this.switchToView}
          useNewPluginMenu={useNewPluginMenu}
          useFontSize={useFontSize}
        />
      </div>
    ) : (
      <Viewer
        isInSEO={isInSEO}
        clickToTweet={clickToTweet}
        mediaSettings={mediaSettings}
        {...commonProps}
      />
    );

    return (
      <div>
        <div className={styles.ricosContainer}>{ViewerEditor}</div>
        {!isEditing && (
          <div className={styles.actionBar}>
            <ActionButton dataHook="ricos-edit-button" text="Edit" onClick={this.onEdit} />
            {showImportExport && (
              <>
                <ActionButton
                  secondary
                  onClick={this.exportContent}
                  text={exportSuccess ? 'Copied to clipboard!' : 'Export'}
                />
                {isImporting ? (
                  <>
                    <ActionButton onClick={this.importContent} text="Import!" />
                    <textarea
                      onChange={this.onImportChange}
                      style={importError ? { background: 'red' } : {}}
                      value={importValue}
                    />
                  </>
                ) : (
                  <ActionButton secondary onClick={this.displayImportPanel} text="Import" />
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default withSettings(RicosDemo);
